// Top bar component - view modes, layer visibility, garden name

import { t } from '@/i18n'
import { debug } from '@/utils/debug'
import {
  topBar,
  topBarGardenName,
  topBarGardenChevron,
  topBarDescDropdown,
  topBarDescTextarea,
  topBar2DBtn,
  topBar3DBtn,
  topBarWalkBtn as topBarWalkBtnEl,
  topBarLayerMapBtn,
  topBarLayerMetadataBtn,
  topBarLayerGroundBtn,
  topBarLayerBuildingsBtn,
  topBarLayerPlantsBtn,
  topBarExitBtn,
  topBarHelpBtn,
  topBarSettingsBtn,
  topBarToolModeGroup,
  topBarToolModeDivider,
  topBarPanBtn,
  topBarSelectBtn,
  topBarRulerBtn,
  topBarUndoGroup,
  topBarUndoDivider,
  topBarUndoBtn,
  topBarRedoBtn,
  topBarClipboardGroup,
  topBarClipboardDivider,
  topBarCopyBtn,
  topBarPasteBtn,
  topBarFileOpsGroup,
  topBarFileOpsDivider,
  topBarNewBtn,
  topBarSaveAsBtn,
  topBarSaveBtn,
  topBarSaveDot
} from './elements'
import { isEditMode } from '@/utils/editMode'
import { isDemoMode } from '@/utils/demoMode'
import { selectToolMode } from './controls'
import { getToolMode } from './state'
import { undoStack } from '@/editor/UndoStack'
import { clipboard } from '@/editor/Clipboard'
import { saveService, type SaveStatus } from '@/api/SaveService'
import { gardenObjectState } from '@/state/GardenObjectState'
import { zoneState } from '@/zones/ZoneState'

// Cast to button for disabled property access
const topBarWalkBtn = topBarWalkBtnEl as HTMLButtonElement
import { getViewer } from './state'

/**
 * Initialize top bar - show it and set up initial state
 */
export function initTopBar(): void {
  debug.log('UI', 'Initializing top bar')

  // Top bar is visible by default via CSS, just ensure it's shown
  topBar.style.display = 'flex'

  // Show file ops, undo/redo, clipboard, and tool mode toggle only in edit mode (not demo)
  if (isEditMode() && !isDemoMode()) {
    topBarFileOpsGroup.style.display = 'flex'
    topBarFileOpsDivider.style.display = 'block'
    topBarUndoGroup.style.display = 'flex'
    topBarUndoDivider.style.display = 'block'
    topBarClipboardGroup.style.display = 'flex'
    topBarClipboardDivider.style.display = 'block'
    topBarToolModeGroup.style.display = 'flex'
    topBarToolModeDivider.style.display = 'block'
  } else if (isEditMode()) {
    // Edit mode in demo — show undo/redo, clipboard, and tool mode, but not file ops
    topBarUndoGroup.style.display = 'flex'
    topBarUndoDivider.style.display = 'block'
    topBarClipboardGroup.style.display = 'flex'
    topBarClipboardDivider.style.display = 'block'
    topBarToolModeGroup.style.display = 'flex'
    topBarToolModeDivider.style.display = 'block'
  }

  if (isEditMode()) {

    // Sync undo/redo button state when stack changes
    undoStack.onChange(updateUndoRedoButtons)
    updateUndoRedoButtons()

    // Sync save button dirty indicator
    gardenObjectState.onChange(updateSaveButton)
    zoneState.onChange(updateSaveButton)
    saveService.onStatusChange(updateSaveButtonStatus)

    // Unsaved changes warning
    window.addEventListener('beforeunload', (e) => {
      if (saveService.hasUnsavedChanges()) {
        e.preventDefault()
      }
    })
  }

  // Update button states
  updateViewModeButtons()
  updateTopBarLayerToggles()
  updateTopBarToolModeButtons()
}

/**
 * Update view mode buttons (2D/3D/Walk) based on viewer state
 */
export function updateViewModeButtons(): void {
  const viewer = getViewer()
  if (!viewer) {
    // Viewer not ready - disable all buttons
    topBar2DBtn.classList.remove('active')
    topBar3DBtn.classList.remove('active')
    topBarWalkBtn.classList.remove('active')
    topBarWalkBtn.disabled = true
    return
  }

  const mode = viewer.getViewMode()
  const isWalking = viewer.isWalkMode()

  // Update 2D/3D buttons
  topBar2DBtn.classList.toggle('active', mode === '2d')
  topBar3DBtn.classList.toggle('active', mode === '3d' && !isWalking)

  // Walk button only enabled in 3D mode
  topBarWalkBtn.disabled = mode !== '3d'
  topBarWalkBtn.classList.toggle('active', isWalking)
}

/**
 * Update layer toggle buttons based on viewer state
 */
export function updateTopBarLayerToggles(): void {
  const viewer = getViewer()
  if (!viewer) return

  const visibility = viewer.getLayerVisibility()

  // Update toggle buttons (order: 1-5 matches keyboard shortcuts)
  const layers: [HTMLElement, boolean][] = [
    [topBarLayerPlantsBtn, visibility.plants],
    [topBarLayerBuildingsBtn, visibility.buildings],
    [topBarLayerGroundBtn, visibility.ground],
    [topBarLayerMetadataBtn, visibility.metadata],
    [topBarLayerMapBtn, visibility.map],
  ]

  for (const [btn, visible] of layers) {
    btn.classList.toggle('active', visible)
    // Update eye overlay icon
    const eyeIcon = btn.querySelector('.layer-eye-icon')
    if (eyeIcon) {
      eyeIcon.classList.toggle('fa-eye', visible)
      eyeIcon.classList.toggle('fa-eye-slash', !visible)
    }
  }
}

// --- Garden name/description state ---
let lastSavedName = ''
let lastSavedDescription = ''

/**
 * Set garden name in top bar (input value)
 */
export function setTopBarGardenName(name: string): void {
  topBarGardenName.value = name
  topBarGardenName.title = name
  lastSavedName = name
}

/**
 * Set garden description in top bar dropdown
 */
export function setTopBarGardenDescription(description: string): void {
  topBarDescTextarea.value = description
  lastSavedDescription = description
}

/**
 * Save garden name/description to API
 */
async function saveGardenNameDescription(): Promise<void> {
  const viewer = getViewer()
  const garden = viewer?.getGarden()
  if (!garden) return

  const name = topBarGardenName.value.trim()
  const description = topBarDescTextarea.value

  // Don't save empty name — revert
  if (!name) {
    topBarGardenName.value = lastSavedName
    return
  }

  // Skip if nothing changed
  if (name === lastSavedName && description === lastSavedDescription) return

  // Update the in-memory garden object immediately
  garden.name = name
  garden.description = description
  topBarGardenName.title = name

  // If garden hasn't been created on server yet (new garden flow),
  // just keep the in-memory change — it'll be sent on first save via createGarden.
  if (!garden.id) {
    lastSavedName = name
    lastSavedDescription = description
    debug.log('UI', `Updated garden name locally: "${name}" (not yet on server)`)
    return
  }

  try {
    const { api } = await import('@/api')
    await api.updateGarden(garden.id, {}, { name, description })
    lastSavedName = name
    lastSavedDescription = description
    debug.log('UI', `Saved garden name: "${name}"`)
  } catch (error) {
    debug.error('UI', 'Failed to save garden name/description', error)
    // Revert on failure
    topBarGardenName.value = lastSavedName
    topBarDescTextarea.value = lastSavedDescription
    garden.name = lastSavedName
    garden.description = lastSavedDescription
  }
}

/**
 * Toggle description dropdown open/close
 */
function toggleDescDropdown(): void {
  const isOpen = topBarDescDropdown.classList.contains('open')
  if (isOpen) {
    closeDescDropdown()
  } else {
    topBarDescDropdown.classList.add('open')
    topBarGardenChevron.innerHTML = '&#9652;' // ▴
    topBarGardenChevron.classList.add('open')
    topBarDescTextarea.focus()
  }
}

/**
 * Close description dropdown and save
 */
function closeDescDropdown(): void {
  if (!topBarDescDropdown.classList.contains('open')) return
  topBarDescDropdown.classList.remove('open')
  topBarGardenChevron.innerHTML = '&#9662;' // ▾
  topBarGardenChevron.classList.remove('open')
  saveGardenNameDescription()
}

/**
 * Set view mode via top bar (2D or 3D)
 */
export function setViewModeFromTopBar(mode: '2d' | '3d'): void {
  const viewer = getViewer()
  if (!viewer) return

  viewer.setViewMode(mode)
  updateViewModeButtons()
  debug.log('UI', `Top bar: View mode set to ${mode}`)
}

/**
 * Toggle walk mode via top bar
 */
export function toggleWalkModeFromTopBar(): void {
  const viewer = getViewer()
  if (!viewer) return

  // Can only toggle walk mode in 3D view
  if (viewer.getViewMode() !== '3d') return

  const newWalkMode = !viewer.isWalkMode()
  viewer.setWalkMode(newWalkMode)
  updateViewModeButtons()
  debug.log('UI', `Top bar: Walk mode toggled to ${newWalkMode}`)
}

/**
 * Toggle layer visibility via top bar
 */
export function toggleLayerFromTopBar(layer: 'plants' | 'buildings' | 'ground' | 'metadata' | 'map'): void {
  const viewer = getViewer()
  if (!viewer) return

  viewer.toggleLayer(layer)
  updateTopBarLayerToggles()
  debug.log('UI', `Top bar: Layer '${layer}' toggled`)
}

/**
 * Update undo/redo button disabled state
 */
export function updateUndoRedoButtons(): void {
  topBarUndoBtn.disabled = !undoStack.canUndo()
  topBarRedoBtn.disabled = !undoStack.canRedo()
}

/**
 * Update clipboard button disabled state based on selection and clipboard content.
 * Called by SelectTool on selection change, copy, and paste.
 * @param hasSelection — whether SelectTool currently has a selected shape
 */
export function updateClipboardButtons(hasSelection?: boolean): void {
  const selected = hasSelection ?? (getViewer()?.getScene()?.getSelectionManager()?.getSelectedResult() != null)
  topBarCopyBtn.disabled = !selected
  topBarPasteBtn.disabled = clipboard.isEmpty()
}

/**
 * Update tool mode buttons (pan/select) based on current state
 */
export function updateTopBarToolModeButtons(): void {
  const mode = getToolMode()

  topBarPanBtn.classList.toggle('active', mode === 'hand')
  topBarSelectBtn.classList.toggle('active', mode === 'pointer')
  topBarRulerBtn.classList.toggle('active', mode === 'ruler')
}

/**
 * Show exit confirmation dialog when there are unsaved changes.
 * Returns the user's choice.
 */
function showExitConfirmDialog(): Promise<'save-and-exit' | 'exit' | 'cancel'> {
  return new Promise((resolve) => {
    let resolved = false
    function finish(result: 'save-and-exit' | 'exit' | 'cancel') {
      if (resolved) return
      resolved = true
      backdrop.remove()
      dialog.remove()
      document.removeEventListener('keydown', onKey)
      resolve(result)
    }

    // Backdrop
    const backdrop = document.createElement('div')
    backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9998;'
    backdrop.addEventListener('click', () => finish('cancel'))

    // Dialog
    const dialog = document.createElement('div')
    dialog.style.cssText = `
      position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
      width:420px;max-width:92vw;
      background:rgba(30,35,45,0.95);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
      border:1px solid rgba(255,255,255,0.1);border-radius:16px;
      box-shadow:0 25px 50px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.1);
      z-index:9999;display:flex;flex-direction:column;overflow:hidden;
    `

    // Header
    const header = document.createElement('div')
    header.style.cssText = 'padding:20px 24px 0;'
    const title = document.createElement('h3')
    title.textContent = t('exit.confirm.title')
    title.style.cssText = 'margin:0;font-size:17px;font-weight:600;color:rgba(255,255,255,0.95);'
    header.appendChild(title)

    const msg = document.createElement('p')
    msg.textContent = t('exit.confirm.message')
    msg.style.cssText = 'margin:10px 0 0;font-size:13px;color:rgba(255,255,255,0.55);line-height:1.4;'
    header.appendChild(msg)
    dialog.appendChild(header)

    // Status row (hidden, shown on save error)
    const statusRow = document.createElement('div')
    statusRow.style.cssText = `
      display:none;align-items:center;gap:8px;margin:12px 24px 0;
      font-size:13px;color:#f76d33;
    `
    const errorIcon = document.createElement('i')
    errorIcon.className = 'fa-solid fa-circle-exclamation'
    errorIcon.style.cssText = 'font-size:14px;'
    const statusText = document.createElement('span')
    statusRow.appendChild(errorIcon)
    statusRow.appendChild(statusText)
    dialog.appendChild(statusRow)

    // Footer with 3 buttons
    const footer = document.createElement('div')
    footer.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;padding:16px 24px 20px;'

    const cancelBtn = document.createElement('button')
    cancelBtn.textContent = t('exit.confirm.cancel')
    cancelBtn.style.cssText = `
      height:34px;padding:0 16px;border:none;border-radius:8px;
      background:rgba(255,255,255,0.1);color:rgba(255,255,255,0.7);
      font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;
      transition:background 0.15s ease;
    `
    cancelBtn.addEventListener('mouseenter', () => { cancelBtn.style.background = 'rgba(255,255,255,0.15)' })
    cancelBtn.addEventListener('mouseleave', () => { cancelBtn.style.background = 'rgba(255,255,255,0.1)' })
    cancelBtn.addEventListener('click', () => finish('cancel'))

    const exitBtn = document.createElement('button')
    exitBtn.textContent = t('exit.confirm.exitWithout')
    exitBtn.style.cssText = `
      height:34px;padding:0 16px;border:none;border-radius:8px;
      background:rgba(247,109,51,0.15);color:#f76d33;
      font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;
      transition:background 0.15s ease;
    `
    exitBtn.addEventListener('mouseenter', () => { exitBtn.style.background = 'rgba(247,109,51,0.25)' })
    exitBtn.addEventListener('mouseleave', () => { exitBtn.style.background = 'rgba(247,109,51,0.15)' })
    exitBtn.addEventListener('click', () => finish('exit'))

    const saveBtn = document.createElement('button')
    saveBtn.textContent = t('exit.confirm.saveAndExit')
    saveBtn.style.cssText = `
      height:34px;padding:0 16px;border:none;border-radius:8px;
      background:rgba(146,204,9,0.2);color:#92cc09;
      font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;
      transition:background 0.15s ease;
    `
    saveBtn.addEventListener('mouseenter', () => { if (!saveBtn.disabled) saveBtn.style.background = 'rgba(146,204,9,0.3)' })
    saveBtn.addEventListener('mouseleave', () => { if (!saveBtn.disabled) saveBtn.style.background = 'rgba(146,204,9,0.2)' })

    saveBtn.addEventListener('click', async () => {
      // Disable all buttons during save
      saveBtn.disabled = true
      exitBtn.disabled = true
      cancelBtn.disabled = true
      saveBtn.textContent = t('exit.confirm.saving')
      saveBtn.style.opacity = '0.5'
      saveBtn.style.cursor = 'not-allowed'
      exitBtn.style.opacity = '0.5'
      exitBtn.style.cursor = 'not-allowed'
      cancelBtn.style.opacity = '0.5'
      cancelBtn.style.cursor = 'not-allowed'

      const viewer = getViewer()
      const garden = viewer?.getGarden()
      if (!garden?.id) {
        finish('exit')
        return
      }
      try {
        const ok = await saveService.save(garden.id, viewer?.getSchemeOutline(), garden)
        if (ok) {
          finish('save-and-exit')
        } else {
          // Save returned false (e.g. already saving) — show error state
          showSaveError()
        }
      } catch {
        showSaveError()
      }
    })

    function showSaveError() {
      statusRow.style.display = 'flex'
      statusText.textContent = t('exit.confirm.saveError')
      // Re-enable exit and cancel buttons so user can choose
      saveBtn.disabled = true
      saveBtn.style.display = 'none'
      exitBtn.disabled = false
      exitBtn.style.opacity = '1'
      exitBtn.style.cursor = 'pointer'
      cancelBtn.disabled = false
      cancelBtn.style.opacity = '1'
      cancelBtn.style.cursor = 'pointer'
    }

    footer.appendChild(cancelBtn)
    footer.appendChild(exitBtn)
    footer.appendChild(saveBtn)
    dialog.appendChild(footer)

    document.body.appendChild(backdrop)
    document.body.appendChild(dialog)

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') finish('cancel')
    }
    document.addEventListener('keydown', onKey)

    // Focus save button for keyboard accessibility
    saveBtn.focus()
  })
}

/**
 * Proceed with the actual exit: reload page without garden param → bootstrap shows selector
 */
async function performExit(): Promise<void> {
  const exitUrl = t('exit.url')
  if (exitUrl) {
    debug.log('UI', `Exiting - redirecting to ${exitUrl}`)
    window.location.href = exitUrl
  } else {
    debug.log('UI', 'Exiting - reloading to garden selector')
    const url = new URL(window.location.href)
    url.searchParams.delete('garden')
    url.searchParams.delete('new')
    window.location.href = url.toString()
  }
}

/**
 * Handle exit button click - returns to garden selector or main site (demo mode)
 */
export async function handleExitClick(): Promise<void> {
  // In demo mode, redirect to main site instead of garden selector
  if (isDemoMode()) {
    debug.log('UI', 'Exit button clicked in demo mode - redirecting to main site')
    window.location.href = 'https://zaprojektuj-ogrod.pl'
    return
  }

  debug.log('UI', 'Exit button clicked')

  // If there are unsaved changes, show confirmation dialog
  if (saveService.hasUnsavedChanges()) {
    const choice = await showExitConfirmDialog()
    if (choice === 'cancel') {
      debug.log('UI', 'Exit cancelled by user')
      return
    }
    // 'save-and-exit' already saved in the dialog; 'exit' means discard
  }

  await performExit()
}

/**
 * Update save button dirty indicator
 */
function updateSaveButton(): void {
  const isDirty = saveService.hasUnsavedChanges()
  topBarSaveDot.style.display = isDirty ? 'block' : 'none'
}

/**
 * Update save button appearance based on save status
 */
function updateSaveButtonStatus(status: SaveStatus): void {
  topBarSaveBtn.classList.remove('saving', 'success', 'error')
  topBarSaveBtn.disabled = false

  switch (status) {
    case 'saving':
      topBarSaveBtn.classList.add('saving')
      topBarSaveBtn.disabled = true
      break
    case 'success':
      topBarSaveBtn.classList.add('success')
      updateSaveButton() // Update dirty dot
      break
    case 'error':
      topBarSaveBtn.classList.add('error')
      break
  }
}

/**
 * Handle "New" button click — navigate to new garden flow
 */
function handleNewClick(): void {
  debug.log('UI', 'New garden button clicked')
  const url = new URL(window.location.href)
  url.searchParams.delete('garden')
  url.searchParams.set('new', 'true')
  url.searchParams.set('edit', 'true')
  window.location.href = url.toString()
}

/**
 * Handle "Save As" button click — show dialog and run the duplicate flow.
 * The dialog stays open with status updates throughout the process.
 */
export async function handleSaveAsClick(): Promise<void> {
  const viewer = getViewer()
  const garden = viewer?.getGarden()
  if (!viewer || !garden || !garden.id) {
    debug.warn('UI', 'Cannot duplicate: no garden or garden not yet saved')
    return
  }

  debug.log('UI', 'Save As button clicked')

  // Capture narrowed references for use in async closure
  const gardenId = garden.id
  const gardenName = garden.name
  const defaultName = t('duplicate.defaultName').replace('{name}', gardenName)

  // --- Build dialog DOM ---

  const backdrop = document.createElement('div')
  backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9998;'

  const dialog = document.createElement('div')
  dialog.style.cssText = `
    position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
    width:420px;max-width:92vw;
    background:rgba(30,35,45,0.95);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
    border:1px solid rgba(255,255,255,0.1);border-radius:16px;
    box-shadow:0 25px 50px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.1);
    z-index:9999;display:flex;flex-direction:column;overflow:hidden;
  `

  // Header
  const header = document.createElement('div')
  header.style.cssText = 'padding:20px 24px 0;'
  const title = document.createElement('h3')
  title.textContent = t('duplicate.title')
  title.style.cssText = 'margin:0;font-size:17px;font-weight:600;color:rgba(255,255,255,0.95);'
  header.appendChild(title)

  const desc = document.createElement('p')
  desc.textContent = t('duplicate.description')
  desc.style.cssText = 'margin:10px 0 0;font-size:13px;color:rgba(255,255,255,0.55);line-height:1.4;'
  header.appendChild(desc)
  dialog.appendChild(header)

  // Body — input + status area
  const body = document.createElement('div')
  body.style.cssText = 'padding:16px 24px;'

  const input = document.createElement('input')
  input.type = 'text'
  input.value = defaultName
  input.placeholder = t('duplicate.namePlaceholder')
  input.style.cssText = `
    width:100%;box-sizing:border-box;padding:10px 12px;
    background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.12);border-radius:8px;
    color:rgba(255,255,255,0.9);font-size:14px;font-family:inherit;outline:none;
    transition:border-color 0.15s ease;
  `
  input.addEventListener('focus', () => { input.style.borderColor = 'rgba(255,255,255,0.25)' })
  input.addEventListener('blur', () => { input.style.borderColor = 'rgba(255,255,255,0.12)' })
  body.appendChild(input)

  // Status row (hidden initially, shown during progress)
  const statusRow = document.createElement('div')
  statusRow.style.cssText = `
    display:none;align-items:center;gap:8px;margin-top:12px;
    font-size:13px;color:rgba(255,255,255,0.7);
  `
  const spinner = document.createElement('i')
  spinner.className = 'fa-solid fa-spinner fa-spin'
  spinner.style.cssText = 'font-size:14px;color:rgba(146,204,9,0.8);'
  const statusText = document.createElement('span')
  statusRow.appendChild(spinner)
  statusRow.appendChild(statusText)
  body.appendChild(statusRow)

  dialog.appendChild(body)

  // Footer
  const footer = document.createElement('div')
  footer.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;padding:0 24px 20px;'

  const cancelBtn = document.createElement('button')
  cancelBtn.textContent = t('duplicate.cancel')
  cancelBtn.style.cssText = `
    height:34px;padding:0 16px;border:none;border-radius:8px;
    background:rgba(255,255,255,0.1);color:rgba(255,255,255,0.7);
    font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;
    transition:background 0.15s ease;
  `
  cancelBtn.addEventListener('mouseenter', () => { cancelBtn.style.background = 'rgba(255,255,255,0.15)' })
  cancelBtn.addEventListener('mouseleave', () => { cancelBtn.style.background = 'rgba(255,255,255,0.1)' })

  const confirmBtn = document.createElement('button')
  confirmBtn.textContent = t('duplicate.confirm')
  confirmBtn.style.cssText = `
    height:34px;padding:0 16px;border:none;border-radius:8px;
    background:rgba(146,204,9,0.2);color:#92cc09;
    font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;
    transition:background 0.15s ease;
  `
  confirmBtn.addEventListener('mouseenter', () => { if (!confirmBtn.disabled) confirmBtn.style.background = 'rgba(146,204,9,0.3)' })
  confirmBtn.addEventListener('mouseleave', () => { if (!confirmBtn.disabled) confirmBtn.style.background = 'rgba(146,204,9,0.2)' })

  footer.appendChild(cancelBtn)
  footer.appendChild(confirmBtn)
  dialog.appendChild(footer)

  document.body.appendChild(backdrop)
  document.body.appendChild(dialog)
  input.focus()
  input.select()

  // --- Helpers ---

  function closeDialog() {
    backdrop.remove()
    dialog.remove()
  }

  function setStatus(msg: string) {
    statusRow.style.display = 'flex'
    statusText.textContent = msg
  }

  function setBusy(busy: boolean) {
    input.disabled = busy
    confirmBtn.disabled = busy
    cancelBtn.disabled = busy
    if (busy) {
      confirmBtn.style.opacity = '0.5'
      confirmBtn.style.cursor = 'not-allowed'
      cancelBtn.style.opacity = '0.5'
      cancelBtn.style.cursor = 'not-allowed'
    } else {
      confirmBtn.style.opacity = '1'
      confirmBtn.style.cursor = 'pointer'
      cancelBtn.style.opacity = '1'
      cancelBtn.style.cursor = 'pointer'
    }
  }

  function showError(msg: string) {
    spinner.className = 'fa-solid fa-circle-exclamation'
    spinner.style.color = '#f76d33'
    spinner.style.animation = 'none'
    statusText.textContent = msg
    statusText.style.color = '#f76d33'
    setBusy(false)
  }

  // --- Wire up the flow ---

  let cancelled = false

  async function runDuplicate() {
    const finalName = input.value.trim() || defaultName
    setBusy(true)

    // Step 1: auto-save if dirty
    if (saveService.hasUnsavedChanges()) {
      setStatus(t('duplicate.saving'))
      try {
        await saveService.save(gardenId, viewer!.getSchemeOutline(), garden!)
      } catch (error) {
        debug.error('UI', 'Auto-save before duplicate failed', error)
        showError(t('duplicate.error'))
        return
      }
    }
    if (cancelled) return

    // Step 2: duplicate
    setStatus(t('duplicate.duplicating'))
    let result: { id: string; name: string }
    try {
      const { api } = await import('@/api')
      result = await api.duplicateGarden(gardenId, finalName)
      debug.success('UI', `Garden duplicated: ${result.id} ("${result.name}")`)
    } catch (error) {
      debug.error('UI', 'Failed to duplicate garden', error)
      showError(t('duplicate.error'))
      return
    }
    if (cancelled) return

    // Step 3: navigate (with duplicated flag so bootstrap triggers thumbnail save)
    setStatus(t('duplicate.opening'))
    const url = new URL(window.location.href)
    url.searchParams.set('garden', result.id)
    url.searchParams.set('edit', 'true')
    url.searchParams.set('duplicated', 'true')
    url.searchParams.delete('new')
    window.location.href = url.toString()
  }

  cancelBtn.addEventListener('click', () => { cancelled = true; closeDialog() })
  backdrop.addEventListener('click', () => {
    // Only allow backdrop close when not busy
    if (!input.disabled) { cancelled = true; closeDialog() }
  })
  confirmBtn.addEventListener('click', () => runDuplicate())
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runDuplicate()
    if (e.key === 'Escape' && !input.disabled) { cancelled = true; closeDialog() }
  })
}

/**
 * Handle save button click
 */
async function handleSaveClick(): Promise<void> {
  const viewer = getViewer()
  const garden = viewer?.getGarden()
  if (!garden) {
    debug.warn('UI', 'No garden loaded, cannot save')
    return
  }

  debug.log('UI', 'Save button clicked')
  await saveService.save(garden.id, viewer?.getSchemeOutline(), garden)
}

/**
 * Wire up top bar event listeners
 */
export function setupTopBarListeners(): void {
  // Tool mode buttons (edit mode only)
  topBarPanBtn.addEventListener('click', () => {
    selectToolMode('hand')
    updateTopBarToolModeButtons()
  })
  topBarSelectBtn.addEventListener('click', () => {
    selectToolMode('pointer')
    updateTopBarToolModeButtons()
  })

  // Undo/Redo buttons
  topBarUndoBtn.addEventListener('click', () => undoStack.undo())
  topBarRedoBtn.addEventListener('click', () => undoStack.redo())

  // Clipboard buttons
  topBarCopyBtn.addEventListener('click', () => clipboard.requestCopy())
  topBarPasteBtn.addEventListener('click', () => clipboard.requestPaste())

  // File operations buttons
  topBarNewBtn.addEventListener('click', handleNewClick)
  topBarSaveAsBtn.addEventListener('click', handleSaveAsClick)
  topBarSaveBtn.addEventListener('click', handleSaveClick)

  // View mode buttons
  topBar2DBtn.addEventListener('click', () => setViewModeFromTopBar('2d'))
  topBar3DBtn.addEventListener('click', () => setViewModeFromTopBar('3d'))
  topBarWalkBtn.addEventListener('click', toggleWalkModeFromTopBar)

  // Layer toggle buttons (order: 1-5 matches keyboard shortcuts)
  topBarLayerPlantsBtn.addEventListener('click', () => toggleLayerFromTopBar('plants'))
  topBarLayerBuildingsBtn.addEventListener('click', () => toggleLayerFromTopBar('buildings'))
  topBarLayerGroundBtn.addEventListener('click', () => toggleLayerFromTopBar('ground'))
  topBarLayerMetadataBtn.addEventListener('click', () => toggleLayerFromTopBar('metadata'))
  topBarLayerMapBtn.addEventListener('click', () => toggleLayerFromTopBar('map'))

  // Exit button
  topBarExitBtn.addEventListener('click', handleExitClick)

  // Help button — lazy-load help overlay
  topBarHelpBtn.addEventListener('click', () => {
    import('./HelpOverlay').then(({ openHelp }) => openHelp())
  })

  // Settings button — lazy-load settings modal
  topBarSettingsBtn.addEventListener('click', () => {
    import('./SettingsModal').then(({ openSettingsModal }) => openSettingsModal())
  })

  // Garden name — save on blur and Enter
  topBarGardenName.addEventListener('blur', () => saveGardenNameDescription())
  topBarGardenName.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      topBarGardenName.blur()
    } else if (e.key === 'Escape') {
      topBarGardenName.value = lastSavedName
      topBarGardenName.blur()
    }
  })

  // In demo mode, make name readonly and hide chevron
  if (isDemoMode()) {
    topBarGardenName.readOnly = true
    topBarGardenChevron.style.display = 'none'
  }

  // Chevron — toggle description dropdown
  topBarGardenChevron.addEventListener('click', (e) => {
    e.stopPropagation()
    toggleDescDropdown()
  })

  // Close dropdown on Escape
  topBarDescTextarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      closeDescDropdown()
    }
  })

  // Close dropdown on click outside
  document.addEventListener('mousedown', (e) => {
    if (!topBarDescDropdown.classList.contains('open')) return
    const target = e.target as Node
    if (!topBarDescDropdown.contains(target) && target !== topBarGardenChevron) {
      closeDescDropdown()
    }
  })

  debug.log('UI', 'Top bar listeners set up')
}
